import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import {
  CatalogFilterOptions,
  PagedResult,
  Product,
  ProductDocument,
  ProductImage,
  ProductListItem,
  ProductQuery,
  ProductRequest,
  ProductSpecification,
  SetProductSpecificationRequest
} from '../models/product.model';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${API_BASE_URL}/products`;

  getPaged(query: ProductQuery = {}): Observable<PagedResult<ProductListItem>> {
    if (query.filters?.length) {
      return this.search(query);
    }

    let params = new HttpParams();
    if (query.categoryId) params = params.set('categoryId', query.categoryId);
    if (query.brandId) params = params.set('brandId', query.brandId);
    if (query.minPrice != null) params = params.set('minPrice', query.minPrice);
    if (query.maxPrice != null) params = params.set('maxPrice', query.maxPrice);
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    if (query.page) params = params.set('page', query.page);
    if (query.pageSize) params = params.set('pageSize', query.pageSize);
    if (query.sort) params = params.set('sort', query.sort);
    return this.http.get<PagedResult<ProductListItem>>(this.baseUrl, { params });
  }

  getFilterOptions(): Observable<CatalogFilterOptions> {
    return this.http.get<CatalogFilterOptions>(`${this.baseUrl}/filter-options`);
  }

  search(query: ProductQuery): Observable<PagedResult<ProductListItem>> {
    return this.http.post<PagedResult<ProductListItem>>(`${this.baseUrl}/search`, query);
  }

  getById(id: string): Observable<Product> {
    return this.http.get<Product>(`${this.baseUrl}/${id}`);
  }

  getBySlug(slug: string): Observable<Product> {
    return this.http.get<Product>(`${this.baseUrl}/by-slug/${encodeURIComponent(slug)}`);
  }

  create(request: ProductRequest): Observable<Product> {
    return this.http.post<Product>(this.baseUrl, request);
  }

  update(id: string, request: ProductRequest): Observable<Product> {
    return this.http.put<Product>(`${this.baseUrl}/${id}`, request);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  uploadImages(productId: string, files: File[]): Observable<ProductImage[]> {
    const form = new FormData();
    for (const file of files) {
      form.append('files', file, file.name);
    }
    return this.http.post<ProductImage[]>(`${this.baseUrl}/${productId}/images`, form);
  }

  setPrimaryImage(productId: string, imageId: string): Observable<ProductImage> {
    return this.http.put<ProductImage>(
      `${this.baseUrl}/${productId}/images/${imageId}/primary`,
      {}
    );
  }

  deleteImage(productId: string, imageId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${productId}/images/${imageId}`);
  }

  uploadDocument(productId: string, file: File, title: string): Observable<ProductDocument> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('title', title);
    return this.http.post<ProductDocument>(`${this.baseUrl}/${productId}/documents`, form);
  }

  deleteDocument(productId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${productId}/documents/${documentId}`);
  }

  setSpecifications(
    productId: string,
    specs: SetProductSpecificationRequest[]
  ): Observable<ProductSpecification[]> {
    return this.http.put<ProductSpecification[]>(
      `${this.baseUrl}/${productId}/specifications`,
      specs
    );
  }

  setRelatedProducts(productId: string, relatedProductIds: string[]): Observable<Product> {
    return this.http.put<Product>(
      `${this.baseUrl}/${productId}/related-products`,
      { relatedProductIds }
    );
  }
}
